"use client";
import { searchModel } from "@pulsex/models";
import { postSearch, postElasticSearch, ELASTIC_POSTS_PER_PAGE, } from "@pulsex/services";
import { useInfiniteQuery } from "@tanstack/react-query";
export default function useSearch(config, options) {
    var _a, _b, _c;
    const isElastic = options.engine === "elastic";
    // On the elastic path apply the same minimum the advanced-search form enforces, so stray
    // one-letter queries (crawlers, hand-typed URLs) never reach Elasticsearch, where they are
    // the most expensive kind of query to run. The CMS path keeps its original condition so
    // the typeahead callers in the other apps behave exactly as before.
    const term = (_b = (_a = options.q) === null || _a === void 0 ? void 0 : _a.trim()) !== null && _b !== void 0 ? _b : "";
    // An explicit `enabled` narrows, it does not override: a caller that opts a query in
    // still must not send a term too short to be worth running. The author lookup passes
    // enabled:true, and without the `&&` its query reached Elasticsearch for two letters —
    // the wildcard-heavy case this guard exists to keep out.
    const enabled = ((_c = options.enabled) !== null && _c !== void 0 ? _c : true) && (isElastic ? term.length > 2 : !!options.q);
    const pageSize = options.postsPerPage || ELASTIC_POSTS_PER_PAGE;
    const query = useInfiniteQuery({
        queryKey: [
            "SEARCH",
            options.q,
            options === null || options === void 0 ? void 0 : options.orderBy,
            options.types,
            options.page,
            options.postsPerPage,
            options.from,
            options.to,
            options.authors,
            options.categories,
            options.engine,
            options.searchScope,
            // These change the request body, so they must take part in the cache key: the author
            // lookup and the main search otherwise collapse into one query whenever their other
            // options happen to line up.
            options.includePostTypes,
            options.excludePostTypes,
        ],
        queryFn: ({ pageParam = options.page || 1 }) => isElastic
            ? postElasticSearch(config, Object.assign({}, options), pageParam)
            : postSearch(config, Object.assign({}, options), pageParam),
        refetchOnWindowFocus: false,
        enabled,
        getNextPageParam: (lastPage, pages) => {
            var _a;
            // Compare against the size actually requested; a hard-coded 20 stops early or pages
            // forever as soon as a caller passes postsPerPage.
            if (((_a = lastPage === null || lastPage === void 0 ? void 0 : lastPage.posts) === null || _a === void 0 ? void 0 : _a.length) && lastPage.posts.length < pageSize) {
                return undefined;
            }
            return pages.length + 1;
        },
        retry: 1,
    });
    let mappedData = {
        count: 0,
        posts: [],
        postTypeCounts: null,
    };
    if (query.isSuccess && query.data) {
        query.data.pages.forEach((responseData) => {
            mappedData.posts.push(...(searchModel(config, responseData) || []));
            mappedData.count = responseData.count;
            mappedData.postTypeCounts = responseData.postTypeCounts;
        });
    }
    return {
        isLoading: enabled && query.isLoading,
        data: mappedData,
        fetchNextData: query.fetchNextPage,
        isFetching: query.isFetching,
        isFetchingNextPage: query.isFetchingNextPage,
        hasNextPage: mappedData.posts.length > 0 && query.hasNextPage,
    };
}
